import React, { useEffect, useState } from "react";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FieldOwnershipBadge } from "@/components/common/FieldOwnershipBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DollarSign, Undo2 } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { OfferFormValues } from "../../../schemas/offerFormSchema";
import { RegularOfferIncrementAlternative } from "@/types/campLocation";
import { getDecimalEnums } from "@/services/decimalEnumService";
import { db } from "@/services/firebaseService";
import { Checkbox } from "@/components/ui/checkbox";
import { programPriceStateLabel } from "@/components/admin/locations/LocationFormSections/components/programPriceState";

interface PricingSectionProps {
  form: UseFormReturn<OfferFormValues>;
  markFieldAsModified?: (fieldName: string) => void;
  isFieldModified?: (fieldName: string) => boolean;
  resetFieldToProgram?: (fieldName: string) => void;
  showHeaders?: boolean;
}

export const PricingSection: React.FC<PricingSectionProps> = ({
  form,
  markFieldAsModified = () => {},
  isFieldModified = () => false,
  resetFieldToProgram = () => {},
  showHeaders = true
}) => {
  // Get values from form
  const variation = form.watch("variation") as any;
  const priceOverride = form.watch("priceOverride");
  const priceNoteOverride = form.watch("priceNoteOverride");
  const numDaysOverride = form.watch("numDaysOverride");
  const tier2Override = form.watch("pricingTierPriceOverrides.tier2") as number | null | undefined;
  const tier3Override = form.watch("pricingTierPriceOverrides.tier3") as number | null | undefined;
  const programPricingTiers = variation?.pricingTiers as any;
  const programNumTiers: 0 | 2 | 3 =
    programPricingTiers?.numTiers === 2 ? 2 : programPricingTiers?.numTiers === 3 ? 3 : 0;
  const hasTieredPricing = programNumTiers > 0;

  const tier1Label = String(programPricingTiers?.tier1Label ?? "").trim();
  const tier2Label = String(programPricingTiers?.tier2Label ?? "").trim();
  const tier3Label = String(programPricingTiers?.tier3Label ?? "").trim();

  const withProgramTierLabel = (base: string, programLabel: string) =>
    hasTieredPricing ? `${base} ("${programLabel}")` : base;
  
  // State to track program values for display with unique keys for re-rendering
  const [programPrice, setProgramPrice] = useState<number | null | undefined>(null);
  const [programTier2Price, setProgramTier2Price] = useState<number | null | undefined>(null);
  const [programTier3Price, setProgramTier3Price] = useState<number | null | undefined>(null);
  const [programPriceNote, setProgramPriceNote] = useState<string>("");
  const [programValuesKey, setProgramValuesKey] = useState<number>(0);
  const [programIsAlternative, setProgramIsAlternative] = useState<boolean>(false);
  const [enumsMap, setEnumsMap] = useState<Map<string, any>>(new Map());
  
  // Load decimal enums for alternative pricing detection
  useEffect(() => {
    const loadEnums = async () => {
      try {
        const enums = await getDecimalEnums(db, "offerIncrement");
        const enumMap = new Map();
        enums.forEach(enumItem => {
          enumMap.set(enumItem.id, enumItem);
        });
        setEnumsMap(enumMap);
      } catch (error) {
        console.error("[PricingSection] Error loading decimal enums:", error);
      }
    };
    loadEnums();
  }, []);
  
  // Function to find matching alternative pricing based on numDays
  const findMatchingAlternative = (variationData: any, targetNumDays: number | null): RegularOfferIncrementAlternative | null => {
    if (!variationData?.regularOfferIncrementAlternatives || targetNumDays === null || targetNumDays === undefined) {
      return null;
    }
    
    for (const alternative of variationData.regularOfferIncrementAlternatives) {
      const enumItem = enumsMap.get(alternative.key);
      if (enumItem && enumItem.value === targetNumDays) {
        return alternative;
      }
    }
    
    return null;
  };
  
  // Function to refresh all program values from the variation
  const refreshProgramValues = (variationData?: any) => {
    const currentVariation = variationData || variation;
    
    if (currentVariation) {
      // Get current numDaysOverride to check for alternative pricing
      const currentNumDays = numDaysOverride !== null && numDaysOverride !== undefined 
        ? numDaysOverride 
        : form.getValues("numDaysOverride");
        
      // Check if current numDays matches an alternative pricing option
      const matchingAlternative = findMatchingAlternative(currentVariation, currentNumDays);
      
      let extractedPriceRegular: number | null;
      let extractedPriceNote: string;
      let isAlternative: boolean;
      
      if (matchingAlternative) {
        // Use alternative pricing
        extractedPriceRegular = matchingAlternative.price;
        const extractedTier2 = matchingAlternative.priceTier2 ?? null;
        const extractedTier3 = matchingAlternative.priceTier3 ?? null;
        extractedPriceNote = matchingAlternative.note || "";
        isAlternative = true;

        setProgramTier2Price(extractedTier2);
        setProgramTier3Price(extractedTier3);
      } else {
        // Use base program pricing
        extractedPriceRegular = currentVariation.priceRegular;
        const extractedTier2 = currentVariation.priceRegularTier2 ?? null;
        const extractedTier3 = currentVariation.priceRegularTier3 ?? null;
        extractedPriceNote = currentVariation.priceNote || "";
        isAlternative = false;

        setProgramTier2Price(extractedTier2);
        setProgramTier3Price(extractedTier3);
      }
      
      
      // Update state values for the program price fields
      setProgramPrice(extractedPriceRegular);
      setProgramPriceNote(extractedPriceNote);
      setProgramIsAlternative(isAlternative);
      
      // Increment the key to force re-renders of elements that show these values
      setProgramValuesKey(prev => prev + 1);
    }
  };
  
  // Listen for variation changes from the custom event
  useEffect(() => {
    const handleVariationChange = (event: CustomEvent) => {
      
      const variationData = event.detail?.variationData;
      if (variationData) {
        // Update our local state with the new program values
        refreshProgramValues(variationData);
        
        // FIXED: Don't auto-populate priceOverride with program values
        // Keep it null unless user explicitly modifies it
        const currentPriceOverride = form.getValues("priceOverride");
        if (currentPriceOverride === undefined) {
          // Set to null explicitly - this means "use program pricing (base or alternative)"
          form.setValue("priceOverride", null);
        }
        
        // FIXED: Similar approach for price note
        const currentPriceNote = form.getValues("priceNoteOverride");
        if (currentPriceNote === undefined) {
          // Set to null explicitly - this means "use program note (base or alternative)"
          form.setValue("priceNoteOverride", null);
        }
      }
    };
    
    window.addEventListener('variationChanged', handleVariationChange as EventListener);
    return () => window.removeEventListener('variationChanged', handleVariationChange as EventListener);
  }, [form, isFieldModified, numDaysOverride, enumsMap]);
  
  // Listen for numDaysOverride changes to detect alternative pricing
  useEffect(() => {
    if (variation && enumsMap.size > 0) {
      refreshProgramValues();
      
      // FIXED: Don't auto-update form values when session length changes
      // The UI displays the correct value through programPrice state
      // Only reset to null if user hasn't explicitly modified the field
      
      // Log current state for debugging without modifying form values
      const currentPriceOverride = form.getValues("priceOverride");
      const currentPriceNote = form.getValues("priceNoteOverride");
    }
  }, [numDaysOverride, variation, enumsMap, programPrice, programPriceNote, programIsAlternative, form, isFieldModified]);
  
  // Update local state when variation changes directly (not through event)
  useEffect(() => {
    if (variation && enumsMap.size > 0) {
      refreshProgramValues();
      
      // FIXED: Only handle undefined case for new forms
      const currentPriceNote = form.getValues("priceNoteOverride");
      if (currentPriceNote === undefined) {
        form.setValue("priceNoteOverride", null);
      }
      
      const currentPriceOverride = form.getValues("priceOverride");
      if (currentPriceOverride === undefined) {
        form.setValue("priceOverride", null);
      }
    }
  }, [variation, enumsMap, numDaysOverride, form]);
  
  // Check if values differ from program values
  const isPriceDifferent = priceOverride !== programPrice && priceOverride !== null && priceOverride !== undefined;
  const isTier2Different =
    hasTieredPricing && programNumTiers >= 2 && tier2Override !== null && tier2Override !== undefined && tier2Override !== programTier2Price;
  const isTier3Different =
    hasTieredPricing && programNumTiers >= 3 && tier3Override !== null && tier3Override !== undefined && tier3Override !== programTier3Price;
  
  // For price note, show reset when: priceNoteOverride is not null AND differs from program value
  const isPriceNoteDifferent = priceNoteOverride !== null && priceNoteOverride !== programPriceNote;
  
  
  // KB-4181 — the program's declared price state, inherited by a session with no price of its own. Shown
  // read-only: it is set on the program, never per session.
  const inheritedPriceState = programPriceStateLabel(variation?.priceState);

  return (
    <div className="space-y-4">
      {showHeaders && <h3 className="text-lg font-medium">Pricing</h3>}
      {inheritedPriceState && (
        <p className="text-sm text-muted-foreground" data-testid="inherited-price-state">
          Price from the program: <span className="font-medium text-foreground">{inheritedPriceState}</span>. Set on the
          program, not here. A price entered below still wins.
        </p>
      )}

      <FormField
        control={form.control}
        name="priceOverride"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FormLabel>
                  {hasTieredPricing
                    ? withProgramTierLabel("Tier 1 regular price", tier1Label)
                    : "Regular price"}
                </FormLabel>
                <FieldOwnershipBadge fieldPath="/priceRegular" />
              </div>
              {/* Only show reset button if priceOverride is not null and differs from program value */}
              {isPriceDifferent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resetFieldToProgram("priceOverride")}
                  className="h-6 px-2"
                >
                  <Undo2 className="h-3 w-3 mr-1" />
                  Reset to program
                </Button>
              )}
            </div>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <DollarSign className="h-4 w-4 text-gray-500" />
              </div>
              <Input
                type="number"
                value={priceOverride !== null && priceOverride !== undefined ? priceOverride : (programPrice ?? "")}
                onChange={e => {
                  const value = e.target.value ? Number(e.target.value) : null;
                  field.onChange(value);
                  markFieldAsModified("priceOverride");
                }}
                placeholder=""
                min="0"
                step="1"
                className="pl-9" 
              />
            </div>
            {/* Enhanced program value footnote that shows alternative vs base pricing */}
            <div className="text-xs text-muted-foreground mt-1" key={`price-${programValuesKey}`}>
              {programIsAlternative 
                ? `Program's alternative value: ${programPrice !== undefined && programPrice !== null ? `$${programPrice}` : "empty"}`
                : `Program's value: ${programPrice !== undefined && programPrice !== null ? `$${programPrice}` : "empty"}`
              }
            </div>
          </FormItem>
        )}
      />
      
      {hasTieredPricing && (
        <div className="space-y-4 pt-2">
          {programNumTiers >= 2 && (
            <FormField
              control={form.control}
              name="pricingTierPriceOverrides.tier2"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>{withProgramTierLabel("Tier 2 price", tier2Label)}</FormLabel>
                    {isTier2Different && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => resetFieldToProgram("pricingTierPriceOverrides.tier2")}
                        className="h-6 px-2"
                      >
                        <Undo2 className="h-3 w-3 mr-1" />
                        Reset to program
                      </Button>
                    )}
                  </div>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <DollarSign className="h-4 w-4 text-gray-500" />
                    </div>
                    <Input
                      type="number"
                      className="pl-9"
                      min="0"
                      step="1"
                      value={field.value !== null && field.value !== undefined ? field.value : (programTier2Price ?? "")}
                      onChange={(e) => {
                        const n = e.target.value === "" ? null : Number(e.target.value);
                        // Ensure the map exists (and is not null) so nested values persist on submit
                        const current = form.getValues("pricingTierPriceOverrides");
                        if (current == null || typeof current !== "object") {
                          form.setValue("pricingTierPriceOverrides", { v: 1 } as any, {
                            shouldDirty: true,
                            shouldTouch: false,
                            shouldValidate: false
                          });
                        }
                        field.onChange(n);
                        markFieldAsModified("pricingTierPriceOverrides");
                      }}
                      placeholder=""
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1" key={`tier2-${programValuesKey}`}>
                    {programIsAlternative
                      ? `Program's alternative value: ${programTier2Price !== undefined && programTier2Price !== null ? `$${programTier2Price}` : "empty"}`
                      : `Program's value: ${programTier2Price !== undefined && programTier2Price !== null ? `$${programTier2Price}` : "empty"}`
                    }
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {programNumTiers >= 3 && (
            <FormField
              control={form.control}
              name="pricingTierPriceOverrides.tier3"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>{withProgramTierLabel("Tier 3 price", tier3Label)}</FormLabel>
                    {isTier3Different && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => resetFieldToProgram("pricingTierPriceOverrides.tier3")}
                        className="h-6 px-2"
                      >
                        <Undo2 className="h-3 w-3 mr-1" />
                        Reset to program
                      </Button>
                    )}
                  </div>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <DollarSign className="h-4 w-4 text-gray-500" />
                    </div>
                    <Input
                      type="number"
                      className="pl-9"
                      min="0"
                      step="1"
                      value={field.value !== null && field.value !== undefined ? field.value : (programTier3Price ?? "")}
                      onChange={(e) => {
                        const n = e.target.value === "" ? null : Number(e.target.value);
                        const current = form.getValues("pricingTierPriceOverrides");
                        if (current == null || typeof current !== "object") {
                          form.setValue("pricingTierPriceOverrides", { v: 1 } as any, {
                            shouldDirty: true,
                            shouldTouch: false,
                            shouldValidate: false
                          });
                        }
                        field.onChange(n);
                        markFieldAsModified("pricingTierPriceOverrides");
                      }}
                      placeholder=""
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1" key={`tier3-${programValuesKey}`}>
                    {programIsAlternative
                      ? `Program's alternative value: ${programTier3Price !== undefined && programTier3Price !== null ? `$${programTier3Price}` : "empty"}`
                      : `Program's value: ${programTier3Price !== undefined && programTier3Price !== null ? `$${programTier3Price}` : "empty"}`
                    }
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      )}

      <FormField
        control={form.control}
        name="priceNoteOverride"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel>Price footnote</FormLabel>
              {/* Show reset button if note is not null and differs from program value */}
              {isPriceNoteDifferent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resetFieldToProgram("priceNoteOverride")}
                  className="h-6 px-2"
                >
                  <Undo2 className="h-3 w-3 mr-1" />
                  Reset to program
                </Button>
              )}
            </div>
            <Input
              value={
                // null = show program value, anything else = show the actual value (including "")
                field.value === null ? programPriceNote : (field.value || "")
              }
              onChange={e => {
                field.onChange(e.target.value);
                markFieldAsModified("priceNoteOverride");
              }}
              placeholder=""
              maxLength={200}
            />
            {/* Enhanced footnote that shows alternative vs base program value */}
            <div className="text-xs text-muted-foreground mt-1" key={`note-${programValuesKey}`}>
              {programIsAlternative 
                ? `Program's alternative value: ${programPriceNote ? `"${programPriceNote}"` : "empty"}`
                : `Program's value: ${programPriceNote ? `"${programPriceNote}"` : "empty"}`
              }
              {field.value !== null && (
                <span className="ml-2">
                  | Current override: {field.value === "" ? "empty string" : `"${field.value}"`}
                </span>
              )}
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};
